import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { fetchProfile, patchProfile } from '@zr/utilities/api/profile';
import withErrorContainer from '@zr/components/withErrorContainer';
import LoadingAnimation from '@zr/components/LoadingAnimation';

import { RDB_OPT_IN } from '@zr/utilities/profileFields';

import PreviewProfile from './PreviewProfile';
import RDBSection from './RDBSection';
import ProfileApprovalDefaultTitle from './ProfileApprovalDefaultTitle';

import './ProfileApproval.less';

export function ProfileApproval({
  children, headerPriority, onContinue, onEdit, onError, onFatalError, onToggleRdb, setIsLoading,
}) {
  const SubHTag = `h${headerPriority + 1}`;

  // disable buttons and rdb input if continue/edit is patching/doing stuff
  const [disabled, setDisabled] = useState(false);
  const [profile, setProfile] = useState(null);
  const [rdbOptIn, setRdbOptIn] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const fetchedProfile = await fetchProfile();
        setProfile(fetchedProfile);
      } catch (e) {
        // if we get here, there is nothing to show, so the jobseeker is kind of stuck
        // have the parent component decide what to do
        onFatalError(e);
      }
    })();
  }, [onFatalError]);

  const patchRdbOptIn = async () => {
    setDisabled(true);
    await patchProfile({
      [RDB_OPT_IN]: rdbOptIn,
    });
  };

  const handleContinue = async () => {
    try {
      // `setIsLoading(true)` so that we can disable the close button while the
      // screen is processing/loading
      setIsLoading(true);
      await patchRdbOptIn();
      await onContinue();
    } catch (e) {
      // if something happened, let them try again (as opposed to fatal error)
      onError(e);
      setDisabled(false);
    }
  };

  const handleRdbChange = (value) => {
    onToggleRdb(value);
    setRdbOptIn(value);
  };

  const handleEdit = async () => {
    try {
      // `setIsLoading(true)` so that we can disable the close button while the
      // screen is processing/loading
      setIsLoading(true);
      await patchRdbOptIn();
      // run callback, probably for telemetry since we will redirect now
      onEdit();
      // a future project will implement inline editing here instead of redirecting to their profile page
      // /profile/edit skips any immediate modals on just /profile like skill verification modal
      window.location.assign('/profile/edit');
    } catch (e) {
      // if something happened, let them try again (as opposed to fatal error)
      onError(e);
      setDisabled(false);
    }
  };

  return (
    <div className="ProfileApproval">
      {!profile && (
        <LoadingAnimation loadingMessage="Please wait..." />
      )}
      {profile && (
        <>
          <header className="profile_approval_header">
            {children}
            <div className="buttons">
              <button
                className="edit_button button"
                disabled={disabled}
                onClick={handleEdit}
                type="button"
              >
                Edit
              </button>
              <button
                className="continue_button button"
                disabled={disabled}
                onClick={handleContinue}
                type="button"
              >
                Continue
              </button>
            </div>
          </header>
          <div className="profile_wrapper">
            <RDBSection
              disabled={disabled}
              onChange={handleRdbChange}
            />
            <PreviewProfile
              headingLevel={SubHTag}
              profileData={profile}
            />
          </div>
        </>
      )}
    </div>
  );
}

ProfileApproval.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
    PropTypes.arrayOf(PropTypes.node),
  ]),
  headerPriority: PropTypes.oneOf([1, 2, 3, 4, 5]),
  onContinue: PropTypes.func.isRequired,
  onEdit: PropTypes.func,
  onError: PropTypes.func.isRequired,
  onFatalError: PropTypes.func.isRequired,
  onToggleRdb: PropTypes.func,
  setIsLoading: PropTypes.func,
};

ProfileApproval.defaultProps = {
  children: <ProfileApprovalDefaultTitle />,
  headerPriority: 4,
  onEdit: () => {},
  onToggleRdb: () => {},
  setIsLoading: () => {},
};

export default withErrorContainer(ProfileApproval);
